import type { Location, Path, To } from 'react-router';
import { parsePath } from 'react-router';

import { useStableCallback } from '@adflow/frontend/util-common';

import { useDispatchNavAction } from '../nav-action';

export interface NavigateOptions {
	replace?: boolean;
	state?: Location['state'];

	/**
	 * If `true`, keep all location fields that are undefined in the arguments.
	 *
	 * For example, `navigate('/foo/bar')` only specifies a `pathname`
	 * so the current `search`, `hash`, and `state` values will not be changed.
	 *
	 * @default false
	 */
	merge?: boolean;
}

export type NavigateFunction = (
	to:
		| To
		| ((
				location: Pick<Location, 'pathname' | 'search' | 'hash' | 'state'>
		  ) => To | (Partial<Path> & NavigateOptions)),
	options?: NavigateOptions
) => void;

/**
 * An improved version of useNavigate.
 *
 * @deprecated Use `useDispatchNavAction` instead.
 */
export function useBetterNavigate(hookOptions?: NavigateOptions) {
	const dispatchNavAction = useDispatchNavAction();

	return useStableCallback<NavigateFunction>((to, callbackOptions) => {
		dispatchNavAction((location) => {
			const stringOrPath = typeof to === 'function' ? to(location) : to;

			// Parse the path so that we always work with the separated path components.
			const path =
				typeof stringOrPath === 'string'
					? parsePath(stringOrPath)
					: stringOrPath;

			const options = { ...hookOptions, ...callbackOptions, ...path };

			if (options.merge) {
				return {
					pathname: path.pathname ?? location.pathname,
					search: path.search ?? location.search,
					hash: path.hash ?? location.hash,

					// State can be set to null so we must not use the ?? operator.
					state: options.state === undefined ? location.state : options.state,
					replace: options.replace,
				};
			} else {
				return {
					...path,
					state: options.state,
					replace: options.replace,
				};
			}
		});
	});
}
